import React, { useState } from "react";
import moment from "moment";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import { MdDownload } from "react-icons/md";
import toast from "react-hot-toast";
import TransactionHistoryServices from "../../../services/TransactionHistoryServices";

const TransactionHistoryDownload = ({
  from,
  to,
  userId,
  q,
  gameType,
  userName = "",
  disabled,
}) => {
  const [downloading, setDownloading] = useState(null);
  const txApi = new TransactionHistoryServices();

  const fetchAllData = async () => {
    return await txApi.getTransactionHistoryForExport({
      q,
      from,
      to,
      userId,
      gameType,
    });
  };

  const downloadExcel = async () => {
    setDownloading("excel");
    try {
      const data = await fetchAllData();
      if (!data?.length) {
        toast.error("No transaction history data to download");
        return;
      }
      const rows = data.map((r, idx) => ({
        "Sr. No.": idx + 1,
        "User Name": userName || "-",
        Date: r.created_at
          ? moment.utc(r.created_at).local().format("DD/MM/YYYY hh:mm A")
          : "—",
        Session: r.game_type || "-",
        Purchase: r.purchase_amount ?? 0,
        Win: r.win_amount ?? 0,
        "Balance Before": r.balance_before_purchase ?? "-",
        "Balance After": r.balance_after_purchase ?? "-",
        Barcode: r.bar_code || "-",
        Status: r.isScan ? r.isScan === 1 ? "Scanned" : r.isScan === 2 ? "Expired" : "Pending" : "-",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Transaction History");
      const safeName = (userName || "all").replace(/[^a-zA-Z0-9_-]/g, "_");
      const fileName = `transaction_history_${safeName}_${from}_to_${to}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success("Excel downloaded");
    } catch (err) {
      console.error("Excel download error:", err);
      toast.error(err?.message || "Failed to download Excel");
    } finally {
      setDownloading(null);
    }
  };

  const downloadPDF = async () => {
    setDownloading("pdf");
    try {
      const data = await fetchAllData();
      if (!data?.length) {
        toast.error("No transaction history data to download");
        return;
      }
      const doc = new jsPDF({ orientation: "portrait", unit: "mm" });
      const pageWidth = doc.internal.pageSize.getWidth();
      let y = 15;

      doc.setFontSize(14);
      doc.text("Transaction History", pageWidth / 2, y, { align: "center" });
      y += 8;
      doc.setFontSize(10);
      doc.text(
        `User: ${userName || "All"} | From: ${from} to To: ${to}${gameType ? ` | Session: ${gameType}` : ""}`,
        pageWidth / 2,
        y,
        { align: "center" },
      );
      y += 14;

      const tableData = data.map((r, idx) => [
        idx + 1,
        r.created_at
          ? moment.utc(r.created_at).local().format("DD/MM/YYYY hh:mm A")
          : "—",
        r.game_type || "-",
        r.purchase_amount ?? 0,
        r.win_amount ?? 0,
        r.balance_after_purchase ?? "-",
        r.bar_code || "-",
        r.isScan ? r.isScan === 1 ? "Scanned" : r.isScan === 2 ? "Expired" : "Pending" : "-",
      ]);

      autoTable(doc, {
        startY: y,
        head: [
          [
            "Sr.",
            "Date",
            "Session",
            "Purchase",
            "Win",
            "Balance",
            "Barcode",
            "Status",
          ],
        ],
        body: tableData,
        theme: "grid",
        margin: { left: 14 },
        tableWidth: pageWidth - 28,
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 32 },
          2: { cellWidth: 18 },
          3: { cellWidth: 22 },
          4: { cellWidth: 22 },
          5: { cellWidth: 22 },
          6: { cellWidth: 35 },
          7: { cellWidth: 22 },
        },
      });

      const safeName = (userName || "all").replace(/[^a-zA-Z0-9_-]/g, "_");
      const fileName = `transaction_history_${safeName}_${from}_to_${to}.pdf`;
      doc.save(fileName);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error("PDF download error:", err);
      toast.error(err?.message || "Failed to download PDF");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="report-download-wrap">
      <div className="report-download-hover-wrap">
        <div className="report-download-trigger">
          <MdDownload size={20} />        
        </div>
        <div className="report-download-dropdown">
          <button
            type="button"
            className="report-download-option"
            onClick={downloadExcel}
            disabled={disabled}
            title="Download Excel"
          >
            <MdDownload size={16} />
            <span>{downloading === "excel" ? "Downloading..." : "Excel"}</span>
          </button>
          <button
            type="button"
            className="report-download-option"
            onClick={downloadPDF}
            disabled={disabled}
            title="Download PDF"
          >
            <MdDownload size={16} />
            <span>{downloading === "pdf" ? "Downloading..." : "PDF"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistoryDownload;
